import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout, COMPANY } from "@/components/site/LegalLayout";
import {
  BUSINESS_PAYMENT_METHODS,
  CONSUMER_PAYMENT_METHODS,
  type PaymentMethod,
} from "@/lib/commerce";

export const Route = createFileRoute("/betaalmethoden")({
  head: () => ({
    meta: [
      { title: "Betaalmethoden & betaalvoorwaarden — Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Alle betaalmethoden bij Elvion Group Nederland: iDEAL, Bancontact, creditcard, Apple Pay, Google Pay, SEPA en factuur. Veilig betalen via Stripe met SSL en 3D Secure.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Betaalmethoden — Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Bekijk alle geaccepteerde betaalmethoden en betaalvoorwaarden bij Elvion Group Nederland.",
      },
      { property: "og:url", content: "https://elvion-group.nl/betaalmethoden" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Betaalmethoden — Elvion Group Nederland" },
      {
        name: "twitter:description",
        content: "Alle geaccepteerde betaalmethoden en veilige betaalverwerking via Stripe.",
      },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/betaalmethoden" }],
  }),
  component: BetaalmethodenPage,
});

type Method = PaymentMethod;

const CONSUMER = CONSUMER_PAYMENT_METHODS;
const BUSINESS = BUSINESS_PAYMENT_METHODS;

function BetaalmethodenPage() {
  return (
    <LegalLayout
      title="Betaalmethoden & betaalvoorwaarden"
      intro="Wij bieden een breed scala aan veilige betaalmethoden. Alle online betalingen worden verwerkt door Stripe Payments Europe, Ltd. via een beveiligde SSL/TLS-verbinding met 3D Secure-authenticatie."
    >
      <h2>1. Betaalmethoden voor particulieren</h2>
      <MethodTable rows={CONSUMER} />

      <h2>2. Betaalmethoden voor zakelijke klanten</h2>
      <MethodTable rows={BUSINESS} />
      <p>
        Zakelijke betalingen op factuur zijn beschikbaar na verificatie van uw
        KvK-inschrijving. U kunt uw bedrijfsgegevens indienen via uw
        klantaccount onder <em>Bedrijfsgegevens</em>.
      </p>

      <h2>3. Veiligheid & privacy</h2>
      <ul>
        <li><strong>SSL/TLS-versleuteling</strong> op alle betaalpagina's (padlock-icoon in uw browser).</li>
        <li><strong>3D Secure 2</strong> voor creditcardbetalingen — extra verificatie via uw bank.</li>
        <li><strong>PCI-DSS Level 1</strong> gecertificeerde verwerking via Stripe. Wij slaan zelf geen kaartgegevens op.</li>
        <li><strong>Fraudedetectie</strong> door Stripe Radar op elke transactie.</li>
      </ul>

      <h2>4. Prijzen &amp; btw</h2>
      <p>
        Alle prijzen op de website zijn <strong>inclusief 21% btw</strong> en in euro's (EUR).
        Zakelijke klanten met een geldig btw-nummer kunnen bij het afrekenen de excl. btw-prijs
        raadplegen. Op de factuur is de btw altijd afzonderlijk gespecificeerd.
      </p>

      <h2>5. Wanneer wordt afgeschreven?</h2>
      <ul>
        <li><strong>iDEAL / Bancontact / creditcard / wallets:</strong> direct bij de bestelling.</li>
        <li><strong>SEPA-overschrijving:</strong> na uw handmatige opdracht — verwerking neemt 1–2 werkdagen.</li>
        <li><strong>Factuur (zakelijk):</strong> binnen 14 dagen na factuurdatum.</li>
      </ul>

      <h2>6. Bevestiging &amp; factuur</h2>
      <p>
        Na een geslaagde betaling ontvangt u binnen enkele minuten een orderbevestiging per
        e-mail, met daarin de Stripe-transactie-ID, de gebruikte betaalmethode en een
        PDF-factuur als bijlage. U kunt uw factuur ook downloaden via de order-statuspagina.
      </p>

      <h2>7. Mislukte of geweigerde betaling</h2>
      <p>
        Wordt uw betaling geweigerd? Dan blijft uw bestelling open staan en ontvangt u een
        e-mail met een nieuwe betaallink. Veelvoorkomende oorzaken zijn een verlopen kaart,
        onvoldoende saldo of een 3D Secure-timeout. Kom er niet uit? Neem contact op via{" "}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> of {COMPANY.phone}.
      </p>

      <h2>8. Terugbetaling</h2>
      <p>
        Terugbetalingen worden via dezelfde betaalmethode verwerkt als de oorspronkelijke
        betaling. Reken op 1–5 werkdagen voor iDEAL/Bancontact en 5–10 werkdagen voor
        creditcard. Zie ons <a href="/retourbeleid">retourbeleid</a> voor de volledige procedure.
      </p>

      <h2>9. Betalingsvoorwaarden</h2>
      <p>
        Op alle betalingen zijn onze <a href="/voorwaarden">algemene voorwaarden</a> van
        toepassing. Bij te late betaling van een factuur zijn wij gerechtigd de wettelijke
        (handels)rente en redelijke incassokosten in rekening te brengen conform artikel
        6:96 lid 2 sub c BW en het Besluit vergoeding voor buitengerechtelijke incassokosten.
      </p>

      <h2>10. Verwerker &amp; contact</h2>
      <p>
        Betalingsverwerker: <strong>Stripe Payments Europe, Ltd.</strong> — 1 Grand Canal
        Street Lower, Grand Canal Dock, Dublin, Ierland. Vragen over een specifieke betaling?
        Vermeld altijd uw ordernummer of transactie-ID en mail naar{" "}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
      </p>
    </LegalLayout>
  );
}

function MethodTable({ rows }: { rows: Method[] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2 font-semibold">Methode</th>
            <th className="px-3 py-2 font-semibold">Omschrijving</th>
            <th className="px-3 py-2 font-semibold">Kosten</th>
            <th className="px-3 py-2 font-semibold">Verwerkingstijd</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="px-3 py-2 font-semibold text-primary">{r.name}</td>
              <td className="px-3 py-2">{r.desc}</td>
              <td className="px-3 py-2">{r.fee}</td>
              <td className="px-3 py-2">{r.time}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
